import { cleanTtsText, renderJournalTextSegments, walkStructuredJournal } from '@repo/sdk'
import type { StructuredJournalTree } from '@repo/sdk'

export const TEXT_LOG_SPEECH_KEYS = [
  'player.lastWordOfFirstNight',
  'player.lastWord',
  'player.sheriffCampaignSpeech',
  'speech.playerStart',
  'speech.playerStartGod',
  'tiedVoteSpeech.playerStart',
  'tiedVoteSpeech.playerStartGod',
  'werewolf.discussionPlayer',
] as const

export type TextLogSpeechKey = (typeof TEXT_LOG_SPEECH_KEYS)[number]
export type TextLogPerspective = 'player' | 'god'

export const TEXT_LOG_PLAYER_VOICES = [
  'alloy',
  'ash',
  'coral',
  'echo',
  'fable',
  'onyx',
  'nova',
  'sage',
] as const

export type TextLogPlayerVoice = (typeof TEXT_LOG_PLAYER_VOICES)[number]

export const TEXT_LOG_TTS_MAX_CHARS = 2000

export interface TextLogSpeechReference {
  id: string
  key: TextLogSpeechKey
  speech: string
  playerId?: string
  position?: number
  voiceName: TextLogPlayerVoice
  language: string
  perspective: TextLogPerspective
  eventSeq?: number
  legacyOccurrence?: number
  placement: {
    start: number
    end: number
  }
  headerPlacement: {
    start: number
    end: number
    accentEnd?: number
  }
  ttsEligible: boolean
}

export interface SpeechReferencePlayer {
  id: string
  position: number
}

export interface ExtractTextLogSpeechReferencesInput {
  structuredLog: unknown
  renderedLog: string
  gameId?: string
  perspective: TextLogPerspective
  language?: string
  players?: readonly SpeechReferencePlayer[]
}

type UnknownRecord = Record<string, unknown>

interface FlatEntry {
  entry: UnknownRecord
  flatIndex: number
}

interface V2Segment {
  eventSeqs: number[]
  text: string
  start: number
}

interface SpeechRange {
  start: number
  end: number
}

interface SpeechPlacement {
  speech: SpeechRange
  header: SpeechRange & { accentEnd?: number }
}

const SPEECH_KEY_SET = new Set<string>(TEXT_LOG_SPEECH_KEYS)

function isRecord(value: unknown): value is UnknownRecord {
  return value !== null && typeof value === 'object' && !Array.isArray(value)
}

function isSpeechKey(value: unknown): value is TextLogSpeechKey {
  return typeof value === 'string' && SPEECH_KEY_SET.has(value.trim())
}

function readSpeech(entry: UnknownRecord): string | null {
  if (!isRecord(entry.vars) || typeof entry.vars.speech !== 'string')
    return null
  const speech = entry.vars.speech.trim()
  return speech || null
}

function flattenEntries(log: UnknownRecord, requireEventSeq: boolean): FlatEntry[] | null {
  if (!isRecord(log.init) || !Array.isArray(log.rounds))
    return null

  try {
    const entries = walkStructuredJournal<unknown>(log as StructuredJournalTree<unknown>, {
      includeResult: true,
      onInit(init) {
        if (!isRecord(init) || !Array.isArray(init.entries))
          throw new Error('Invalid structured journal init')
      },
      onRound(round) {
        if (!isRecord(round) || !Array.isArray(round.phases))
          throw new Error('Invalid structured journal round')
      },
      onPhase(phase) {
        if (!isRecord(phase) || !Array.isArray(phase.subPhases))
          throw new Error('Invalid structured journal phase')
      },
      onSubPhase(subPhase) {
        if (!isRecord(subPhase) || !Array.isArray(subPhase.entries))
          throw new Error('Invalid structured journal subphase')
      },
      onResult(result) {
        if (result !== undefined && !isRecord(result))
          throw new Error('Invalid structured journal result')
      },
    })
    const seen = new Set<number>()
    return entries.map(({ entry }, flatIndex) => {
      if (!isRecord(entry))
        throw new Error('Invalid structured journal entry')
      if (requireEventSeq) {
        if (!Number.isSafeInteger(entry.eventSeq) || (entry.eventSeq as number) <= 0)
          throw new Error('Invalid structured journal sequence')
        const eventSeq = entry.eventSeq as number
        if (seen.has(eventSeq))
          throw new Error('Duplicate structured journal sequence')
        seen.add(eventSeq)
      }
      return { entry, flatIndex }
    })
  }
  catch {
    return null
  }
}

function findStandaloneSpeechRange(text: string, speech: string): SpeechRange | null {
  const candidates: SpeechRange[] = []
  let cursor = 0
  while (cursor <= text.length - speech.length) {
    const start = text.indexOf(speech, cursor)
    if (start === -1)
      break
    const end = start + speech.length
    const lineStart = text.lastIndexOf('\n', Math.max(0, start - 1)) + 1
    const nextNewline = text.indexOf('\n', end)
    const lineEnd = nextNewline === -1 ? text.length : nextNewline
    if (
      text.slice(lineStart, start).trim() === ''
      && text.slice(end, lineEnd).trim() === ''
    ) {
      candidates.push({ start, end })
    }
    cursor = start + Math.max(1, speech.length)
  }
  return candidates.length === 1 ? candidates[0]! : null
}

function findSpeechPlacement(
  text: string,
  speech: string,
): SpeechPlacement | null {
  const speechRange = findStandaloneSpeechRange(text, speech)
  if (!speechRange)
    return null

  const speechLineStart
    = text.lastIndexOf('\n', Math.max(0, speechRange.start - 1)) + 1
  let headerEnd = speechLineStart
  while (headerEnd > 0 && /\s/u.test(text[headerEnd - 1]!))
    headerEnd--
  if (headerEnd === 0)
    return null

  const headerStart = text.lastIndexOf('\n', headerEnd - 1) + 1
  if (!text.slice(headerStart, headerEnd).trim())
    return null

  const speechIcon = '💬'
  const iconIndex = text.slice(headerStart, headerEnd).indexOf(speechIcon)
  const accentEnd = iconIndex === -1
    ? undefined
    : headerStart + iconIndex + speechIcon.length

  return {
    speech: speechRange,
    header: {
      start: headerStart,
      end: headerEnd,
      ...(accentEnd === undefined ? {} : { accentEnd }),
    },
  }
}

function getSpeaker(entry: UnknownRecord): { playerId?: string, position?: number } {
  if (!isRecord(entry.vars) || !isRecord(entry.vars.player))
    return {}
  const { player } = entry.vars
  const playerId = typeof player.playerId === 'string' && player.playerId
    ? player.playerId
    : undefined
  const position = Number.isSafeInteger(player.position) && (player.position as number) > 0
    ? player.position as number
    : undefined
  return { playerId, position }
}

function buildVoiceMap(players: readonly SpeechReferencePlayer[] | undefined): Map<string, TextLogPlayerVoice> {
  if (!players)
    return new Map()
  const sorted = players
    .filter(player => player.id && Number.isSafeInteger(player.position))
    .slice()
    .sort((left, right) => left.position - right.position || left.id.localeCompare(right.id))
  return new Map(sorted.map((player, index) => [
    player.id,
    TEXT_LOG_PLAYER_VOICES[index % TEXT_LOG_PLAYER_VOICES.length]!,
  ]))
}

function resolveVoice(
  speaker: { playerId?: string, position?: number },
  voiceMap: ReadonlyMap<string, TextLogPlayerVoice>,
): TextLogPlayerVoice {
  if (speaker.playerId && voiceMap.has(speaker.playerId))
    return voiceMap.get(speaker.playerId)!
  if (speaker.position && speaker.position > 0) {
    return TEXT_LOG_PLAYER_VOICES[
      (speaker.position - 1) % TEXT_LOG_PLAYER_VOICES.length
    ]!
  }
  return 'alloy'
}

function makeReference(input: {
  gameId: string
  perspective: TextLogPerspective
  identity: string
  key: TextLogSpeechKey
  speech: string
  entry: UnknownRecord
  placement: SpeechRange
  headerPlacement: SpeechRange & { accentEnd?: number }
  language: string
  voiceMap: ReadonlyMap<string, TextLogPlayerVoice>
  eventSeq?: number
  legacyOccurrence?: number
}): TextLogSpeechReference {
  const speaker = getSpeaker(input.entry)
  return {
    id: `tts:${input.gameId}:${input.perspective}:${input.identity}`,
    key: input.key,
    speech: input.speech,
    ...speaker,
    voiceName: resolveVoice(speaker, input.voiceMap),
    language: input.language,
    perspective: input.perspective,
    ...(input.eventSeq === undefined ? {} : { eventSeq: input.eventSeq }),
    ...(input.legacyOccurrence === undefined
      ? {}
      : { legacyOccurrence: input.legacyOccurrence }),
    placement: input.placement,
    headerPlacement: input.headerPlacement,
    ttsEligible: input.speech.length <= TEXT_LOG_TTS_MAX_CHARS
      && cleanTtsText(input.speech).length > 0,
  }
}

function extractV2(
  log: UnknownRecord,
  input: ExtractTextLogSpeechReferencesInput,
  voiceMap: ReadonlyMap<string, TextLogPlayerVoice>,
): TextLogSpeechReference[] {
  const flatEntries = flattenEntries(log, true)
  if (!flatEntries || !isRecord(log.text) || !Array.isArray(log.text.segments))
    return []

  const segments: V2Segment[] = []
  const eventToSegment = new Map<number, V2Segment>()
  const knownEvents = new Set(flatEntries.map(({ entry }) => entry.eventSeq as number))

  for (const value of log.text.segments) {
    if (
      !isRecord(value)
      || typeof value.text !== 'string'
      || !Array.isArray(value.eventSeqs)
      || value.eventSeqs.length === 0
    ) {
      return []
    }
    const eventSeqs: number[] = []
    const segmentEvents = new Set<number>()
    for (const eventSeq of value.eventSeqs) {
      if (!Number.isSafeInteger(eventSeq) || !knownEvents.has(eventSeq as number))
        return []
      if (
        segmentEvents.has(eventSeq as number)
        || eventToSegment.has(eventSeq as number)
      ) {
        return []
      }
      segmentEvents.add(eventSeq as number)
      eventSeqs.push(eventSeq as number)
    }
    const segment: V2Segment = {
      eventSeqs,
      text: value.text,
      start: 0,
    }
    segments.push(segment)
    for (const eventSeq of eventSeqs)
      eventToSegment.set(eventSeq, segment)
  }

  const renderedMain = renderJournalTextSegments(segments, {
    onSegment(segment, start) {
      segment.start = start
    },
  })

  if (renderedMain && !input.renderedLog.endsWith(renderedMain))
    return []
  const mainStart = renderedMain
    ? input.renderedLog.length - renderedMain.length
    : input.renderedLog.length

  const references: TextLogSpeechReference[] = []
  for (const { entry } of flatEntries) {
    if (!isSpeechKey(entry.key))
      continue
    const speech = readSpeech(entry)
    if (!speech)
      continue
    const eventSeq = entry.eventSeq as number
    const segment = eventToSegment.get(eventSeq)
    if (!segment)
      continue
    const speechPlacement = findSpeechPlacement(segment.text, speech)
    if (!speechPlacement)
      continue
    references.push(makeReference({
      gameId: input.gameId || 'unknown-game',
      perspective: input.perspective,
      identity: `v2:${eventSeq}`,
      key: entry.key.trim() as TextLogSpeechKey,
      speech,
      entry,
      placement: {
        start: mainStart + segment.start + speechPlacement.speech.start,
        end: mainStart + segment.start + speechPlacement.speech.end,
      },
      headerPlacement: {
        start: mainStart + segment.start + speechPlacement.header.start,
        end: mainStart + segment.start + speechPlacement.header.end,
        ...(speechPlacement.header.accentEnd === undefined
          ? {}
          : {
              accentEnd: mainStart
                + segment.start
                + speechPlacement.header.accentEnd,
            }),
      },
      language: input.language || 'en',
      voiceMap,
      eventSeq,
    }))
  }
  return references
}

function stripHeaderHashesWithSourceMap(source: string): { text: string, sourceOffsets: number[] } {
  let text = ''
  const sourceOffsets: number[] = []
  for (let index = 0; index < source.length;) {
    if (source[index] === '#') {
      let end = index
      while (source[end] === '#')
        end++
      if (end - index >= 3) {
        index = end
        continue
      }
    }
    text += source[index]
    sourceOffsets.push(index)
    index++
  }
  return { text, sourceOffsets }
}

function allOccurrences(text: string, needle: string): number[] {
  const result: number[] = []
  let cursor = 0
  while (cursor <= text.length - needle.length) {
    const found = text.indexOf(needle, cursor)
    if (found === -1)
      break
    result.push(found)
    cursor = found + Math.max(1, needle.length)
  }
  return result
}

function extractV1(
  log: UnknownRecord,
  input: ExtractTextLogSpeechReferencesInput,
  voiceMap: ReadonlyMap<string, TextLogPlayerVoice>,
): TextLogSpeechReference[] {
  const flatEntries = flattenEntries(log, false)
  if (!flatEntries)
    return []
  const candidates: Array<{
    entry: UnknownRecord
    flatIndex: number
    key: TextLogSpeechKey
    speech: string
    translation: string
    speechPlacement: SpeechPlacement
  }> = []
  for (const { entry, flatIndex } of flatEntries) {
    if (!isSpeechKey(entry.key))
      continue
    const speech = readSpeech(entry)
    if (!speech)
      continue
    if (typeof entry.translation !== 'string')
      return []
    const translation = entry.translation.trim()
    const speechPlacement = findSpeechPlacement(translation, speech)
    if (!translation || !speechPlacement)
      return []
    candidates.push({
      entry,
      flatIndex,
      key: entry.key.trim() as TextLogSpeechKey,
      speech,
      translation,
      speechPlacement,
    })
  }
  if (candidates.length === 0)
    return []

  const normalized = stripHeaderHashesWithSourceMap(input.renderedLog)
  const groups = new Map<string, typeof candidates>()
  for (const candidate of candidates) {
    const group = groups.get(candidate.translation) ?? []
    group.push(candidate)
    groups.set(candidate.translation, group)
  }

  const mapped: Array<{
    candidate: (typeof candidates)[number]
    placement: SpeechRange
    headerPlacement: SpeechRange & { accentEnd?: number }
  }> = []
  for (const [translation, group] of groups) {
    const occurrences = allOccurrences(normalized.text, translation)
    if (occurrences.length !== group.length)
      return []
    group.forEach((candidate, occurrenceIndex) => {
      const blockStart = occurrences[occurrenceIndex]!
      const normalizedStart = blockStart + candidate.speechPlacement.speech.start
      const normalizedEnd = blockStart + candidate.speechPlacement.speech.end
      const sourceStart = normalized.sourceOffsets[normalizedStart]
      const sourceEndOffset = normalized.sourceOffsets[normalizedEnd - 1]
      if (sourceStart === undefined || sourceEndOffset === undefined)
        return
      const placement = { start: sourceStart, end: sourceEndOffset + 1 }
      if (input.renderedLog.slice(placement.start, placement.end) !== candidate.speech)
        return

      const normalizedHeaderStart
        = blockStart + candidate.speechPlacement.header.start
      const normalizedHeaderEnd
        = blockStart + candidate.speechPlacement.header.end
      const sourceHeaderStart = normalized.sourceOffsets[normalizedHeaderStart]
      const sourceHeaderEndOffset
        = normalized.sourceOffsets[normalizedHeaderEnd - 1]
      if (sourceHeaderStart === undefined || sourceHeaderEndOffset === undefined)
        return
      const normalizedAccentEnd = candidate.speechPlacement.header.accentEnd
      const sourceAccentEndOffset = normalizedAccentEnd === undefined
        ? undefined
        : normalized.sourceOffsets[blockStart + normalizedAccentEnd - 1]
      if (normalizedAccentEnd !== undefined && sourceAccentEndOffset === undefined)
        return

      mapped.push({
        candidate,
        placement,
        headerPlacement: {
          start: sourceHeaderStart,
          end: sourceHeaderEndOffset + 1,
          ...(sourceAccentEndOffset === undefined
            ? {}
            : { accentEnd: sourceAccentEndOffset + 1 }),
        },
      })
    })
    if (mapped.filter(item => group.includes(item.candidate)).length !== group.length)
      return []
  }

  mapped.sort((left, right) => left.candidate.flatIndex - right.candidate.flatIndex)
  for (let index = 1; index < mapped.length; index++) {
    if (mapped[index]!.placement.start <= mapped[index - 1]!.placement.end)
      return []
  }

  return mapped.map(({ candidate, placement, headerPlacement }, legacyOccurrence) =>
    makeReference({
      gameId: input.gameId || 'unknown-game',
      perspective: input.perspective,
      identity: `v1:${candidate.flatIndex}`,
      key: candidate.key,
      speech: candidate.speech,
      entry: candidate.entry,
      placement,
      headerPlacement,
      language: input.language || 'en',
      voiceMap,
      legacyOccurrence,
    }))
}

export function extractTextLogSpeechReferences(
  input: ExtractTextLogSpeechReferencesInput,
): TextLogSpeechReference[] {
  if (!isRecord(input.structuredLog) || typeof input.renderedLog !== 'string')
    return []
  const voiceMap = buildVoiceMap(input.players)
  if (input.structuredLog.schemaVersion === 2)
    return extractV2(input.structuredLog, input, voiceMap)
  if ('schemaVersion' in input.structuredLog)
    return []
  return extractV1(input.structuredLog, input, voiceMap)
}

export function rebaseSpeechReferences(
  references: readonly TextLogSpeechReference[],
  sourceStart: number,
  sourceEnd: number,
): TextLogSpeechReference[] {
  return references
    .filter(reference =>
      reference.placement.start >= sourceStart
      && reference.placement.end <= sourceEnd
      && reference.headerPlacement.start >= sourceStart
      && reference.headerPlacement.end <= sourceEnd)
    .map(reference => ({
      ...reference,
      placement: {
        start: reference.placement.start - sourceStart,
        end: reference.placement.end - sourceStart,
      },
      headerPlacement: {
        start: reference.headerPlacement.start - sourceStart,
        end: reference.headerPlacement.end - sourceStart,
        ...(reference.headerPlacement.accentEnd === undefined
          ? {}
          : {
              accentEnd: reference.headerPlacement.accentEnd - sourceStart,
            }),
      },
    }))
}
